import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { ISupplier } from 'contacts/types';
import { FormProvider } from 'core/states';

import { AlternateCorporatePartAssignVendorFormFields } from './AlternateCorporatePartAssignVendorFormFields';
import { LegacyCorporatePartAssignVendorFormFields } from './LegacyCorporatePartAssignVendorFormFields';
import { corporatePartAssignVendorSchema } from './schema';
import { CorporatePartAssignVendorFormData } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CorporatePartAssignVendorFormData) => Promise<{ success: boolean }>;
  defaultValues?: Partial<CorporatePartAssignVendorFormData>;
  externalSupplier?: Partial<ISupplier>;
  isEditable?: boolean;
  interactive?: boolean;
  isAlternateVersionEnabled?: boolean;
  level?: number;
}

export const CorporatePartAssignVendor = ({
  isOpen,
  onClose,
  onSubmit,
  defaultValues: customDefaultValues = {},
  externalSupplier = null,
  isEditable = false,
  interactive = false,
  isAlternateVersionEnabled = false,
  level = 0,
}: IProps) => {
  const defaultValues = useMemo(() => {
    return {
      ...customDefaultValues,
      vendor: customDefaultValues.vendor || null,
      vendorLocation: customDefaultValues.vendorLocation || null,
    };
  }, [customDefaultValues]);

  const handleOnSubmit =
    (reset: UseFormReset<CorporatePartAssignVendorFormData>) =>
    async (data: CorporatePartAssignVendorFormData) => {
      const { success } = await onSubmit(data);

      if (success) {
        handleOnClose(reset);
      }
    };

  const handleOnClose = (reset: UseFormReset<CorporatePartAssignVendorFormData>) => {
    reset();
    onClose();
  };

  const FormFields = isAlternateVersionEnabled
    ? AlternateCorporatePartAssignVendorFormFields
    : LegacyCorporatePartAssignVendorFormFields;

  return (
    <FormProvider
      schema={corporatePartAssignVendorSchema(isAlternateVersionEnabled)}
      defaultValues={defaultValues}
      resetDefaultValues={!!defaultValues}
    >
      <FormFields
        isOpen={isOpen}
        onSubmit={handleOnSubmit}
        onClose={handleOnClose}
        externalSupplier={externalSupplier}
        disabled={!isEditable}
        interactive={interactive}
        level={level}
      />
    </FormProvider>
  );
};
